// Focus mechanics for the <Modal> shell (UX-audit §5.3 / K-forms #2, #3).
//
// Extracted from Modal.tsx so the branchy parts — the Tab wrap and the
// "what should open focused" choice — are pure functions with their own tests
// rather than nested branches inside a keydown listener.

/** Everything the browser will Tab to. Order matters: querySelectorAll returns
 *  DOCUMENT order, not selector order. */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Real editable fields only — no buttons. Used to pick the opening focus. */
const FIELD_SELECTOR =
  'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])'

const ACTION_SELECTOR = 'button:not([disabled]), a[href]'

/** What should have focus when the modal opens.
 *
 *  Search the BODY, not the whole card, and try real fields before buttons: a
 *  selector list returns the first match in DOCUMENT order, so querying the
 *  card for "input, …, button" hands focus to the header's own X — every modal
 *  would open with Close focused and Enter would dismiss it.
 *
 *  Nothing focusable in the body (a pure read-out) → the card itself, so the
 *  trap has an anchor and the dialog is announced, rather than leaving focus
 *  behind on the trigger under the backdrop. */
export function openingFocusTarget(
  body: HTMLElement | null,
  card: HTMLElement | null,
): HTMLElement | null {
  const field = body?.querySelector<HTMLElement>(FIELD_SELECTOR)
  if (field) return field
  const action = body?.querySelector<HTMLElement>(ACTION_SELECTOR)
  return action ?? card
}

/** Where a Modal's focus goes on open — see Modal's `initialFocus` prop.
 *   • 'auto'  (default) — a mouse/keyboard device gets the first field, so
 *     typing and scanning start at once (K-forms #3). A TOUCH device gets the
 *     dialog itself: focusing a field there raises the on-screen keyboard over
 *     the very dialog the person opened to read — every spray dialog opened on
 *     a decimal field and put the numeric keypad over its tank mix (farm audit
 *     X-12 / SP-06).
 *   • 'field' — the first field on every device: a dialog whose whole job is
 *     one number or one scan opts back in.
 *   • 'card'  — the dialog on every device: read-only sheets, which have
 *     nothing the person came to type (Growth Stages history, CR-10). */
export type InitialFocus = 'auto' | 'field' | 'card'

/** A field that raises the NUMERIC keypad. */
function isNumericField(el: Element): boolean {
  if (el.tagName !== 'INPUT') return false
  const mode = el.getAttribute('inputmode')
  return (el as HTMLInputElement).type === 'number' || mode === 'decimal' || mode === 'numeric'
}

/** Is the primary pointer a finger? The same media query as `coarse:`, and
 *  exactly `useDeviceClass() !== 'desktop'` — phone AND tablet, both of which
 *  should wait for a tap before raising the keyboard. */
export function coarsePointer(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches
}

/** What to focus when the modal opens, or null to leave focus where it is.
 *
 *  `active` is document.activeElement at the time — React applies an explicit
 *  `autoFocus` during commit, BEFORE the modal's effect runs, so a field the
 *  page chose is already focused. That choice is honoured, with one exception
 *  on touch: a numeric field would raise the keypad the moment the dialog
 *  opens, so it waits for a tap unless the dialog opted in with 'field'. */
export function openingFocus(p: {
  body: HTMLElement | null
  card: HTMLElement | null
  active: Element | null
  coarse: boolean
  mode: InitialFocus
}): HTMLElement | null {
  const { body, card, active, coarse, mode } = p
  if (mode === 'card') return card
  const touchDefault = coarse && mode === 'auto'
  if (card && active && card.contains(active)) {
    return touchDefault && isNumericField(active) ? card : null
  }
  return touchDefault ? card : openingFocusTarget(body, card)
}

/** Keep Tab inside `card`, wrapping at both ends. Returns true if it moved
 *  focus (and so called preventDefault). */
export function trapTab(
  e: Pick<KeyboardEvent, 'shiftKey'> & { preventDefault: () => void },
  card: HTMLElement,
  active: Element | null,
): boolean {
  const focusables = card.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  if (focusables.length === 0) return false

  const first = focusables[0]
  const last = focusables[focusables.length - 1]

  const wrapTo = e.shiftKey
    ? (active === first ? last : null)
    : (active === last ? first : null)

  if (!wrapTo) return false
  e.preventDefault()
  wrapTo.focus()
  return true
}
